/*
 * basal.js
 * Main JavaScript for the Basal CBD Shopify theme.
 * Vanilla JS, no dependencies.
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                            */
  /* ------------------------------------------------------------------ */

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (res) {
      if (!res.ok) throw new Error(res.statusText);
      return res.json();
    });
  }

  /* ------------------------------------------------------------------ */
  /*  CartAPI                                                            */
  /* ------------------------------------------------------------------ */

  var CartAPI = {
    addToCart: function (variantId, quantity) {
      return fetchJSON('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity || 1 }),
      });
    },

    updateCart: function (key, quantity) {
      return fetchJSON('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity }),
      });
    },

    getCart: function () {
      return fetchJSON('/cart.js');
    },
  };

  /* ------------------------------------------------------------------ */
  /*  CartDrawer                                                         */
  /* ------------------------------------------------------------------ */

  var CartDrawer = {
    drawer: null,
    backdrop: null,
    _previousFocus: null,

    init: function () {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.backdrop = document.querySelector('[data-cart-backdrop]');
      if (!this.drawer) return;

      /* Close buttons */
      document.querySelectorAll('[data-cart-close]').forEach(
        function (btn) {
          btn.addEventListener('click', this.close.bind(this));
        }.bind(this)
      );

      /* Backdrop click */
      if (this.backdrop) {
        this.backdrop.addEventListener('click', this.close.bind(this));
      }

      /* Escape key */
      document.addEventListener(
        'keydown',
        function (e) {
          if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
            this.close();
          }
        }.bind(this)
      );
    },

    open: function () {
      if (!this.drawer) return;
      this._previousFocus = document.activeElement;
      this.drawer.classList.add('is-open');
      if (this.backdrop) this.backdrop.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      this._trapFocus();
    },

    close: function () {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      if (this.backdrop) this.backdrop.classList.remove('is-open');
      document.body.style.overflow = '';
      if (this._previousFocus) this._previousFocus.focus();
    },

    _trapFocus: function () {
      var focusable = this.drawer.querySelectorAll(
        'button, a[href], input, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      first.focus();

      this.drawer.addEventListener('keydown', function handler(e) {
        if (e.key !== 'Tab') return;
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      });
    },

    render: function (cart) {
      var container = document.querySelector('[data-cart-items]');
      if (!container) return;

      this._updateCounts(cart.item_count);

      if (!cart.items || cart.items.length === 0) {
        container.innerHTML = '<p class="cart-drawer__empty">Your cart is empty</p>';
        var subtotal = document.querySelector('[data-cart-subtotal]');
        if (subtotal) subtotal.textContent = formatMoney(0);
        return;
      }

      var html = '';
      cart.items.forEach(function (item) {
        var imageSrc = item.featured_image
          ? item.featured_image.url
            ? item.featured_image.url + '&width=120'
            : item.featured_image + '&width=120'
          : '';

        var variantMarkup =
          item.variant_title && item.variant_title !== 'Default Title'
            ? '<span class="cart-drawer__item-variant">' + item.variant_title + '</span>'
            : '';

        html +=
          '<div class="cart-drawer__item" data-cart-item-key="' + item.key + '">' +
            '<div class="cart-drawer__item-image">' +
              (imageSrc ? '<img src="' + imageSrc + '" alt="' + item.product_title + '" width="60" height="60" loading="lazy">' : '') +
            '</div>' +
            '<div class="cart-drawer__item-details">' +
              '<span class="cart-drawer__item-title">' + item.product_title + '</span>' +
              variantMarkup +
              '<span class="cart-drawer__item-price">' + formatMoney(item.final_line_price) + '</span>' +
              '<div class="cart-drawer__item-actions">' +
                '<button class="cart-drawer__qty-btn" data-qty-change="-1" data-line-key="' + item.key + '" aria-label="Decrease quantity">&minus;</button>' +
                '<span class="cart-drawer__qty">' + item.quantity + '</span>' +
                '<button class="cart-drawer__qty-btn" data-qty-change="1" data-line-key="' + item.key + '" aria-label="Increase quantity">+</button>' +
                '<button class="cart-drawer__remove" data-remove-key="' + item.key + '" aria-label="Remove ' + item.product_title + '">Remove</button>' +
              '</div>' +
            '</div>' +
          '</div>';
      });

      container.innerHTML = html;

      var subtotal = document.querySelector('[data-cart-subtotal]');
      if (subtotal) subtotal.textContent = formatMoney(cart.total_price);

      /* Quantity change buttons */
      container.querySelectorAll('[data-qty-change]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var key = btn.getAttribute('data-line-key');
          var delta = parseInt(btn.getAttribute('data-qty-change'), 10);
          var qtyEl = btn.parentElement.querySelector('.cart-drawer__qty');
          var current = parseInt(qtyEl.textContent, 10);
          var newQty = Math.max(0, current + delta);
          CartAPI.updateCart(key, newQty).then(function (updatedCart) {
            CartDrawer.render(updatedCart);
          });
        });
      });

      /* Remove buttons */
      container.querySelectorAll('[data-remove-key]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var key = btn.getAttribute('data-remove-key');
          CartAPI.updateCart(key, 0).then(function (updatedCart) {
            CartDrawer.render(updatedCart);
          });
        });
      });
    },

    _updateCounts: function (count) {
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
      });
    },

    refresh: function () {
      CartAPI.getCart().then(
        function (cart) {
          this.render(cart);
        }.bind(this)
      );
    },
  };

  /* ------------------------------------------------------------------ */
  /*  VariantSelector (handled by inline script in main-product.liquid)  */
  /* ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------ */
  /*  StickyATC                                                          */
  /* ------------------------------------------------------------------ */

  var StickyATC = {
    init: function () {
      var mainBtn = document.querySelector('[data-atc-button]');
      var stickyBar = document.querySelector('.sticky-atc');
      if (!mainBtn || !stickyBar) return;

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              stickyBar.classList.remove('is-visible');
            } else {
              stickyBar.classList.add('is-visible');
            }
          });
        },
        { threshold: 0 }
      );

      observer.observe(mainBtn);

      /* Sticky ATC button click = submit the product form */
      var stickyBtn = stickyBar.querySelector('.sticky-atc__button');
      if (stickyBtn) {
        stickyBtn.addEventListener('click', function () {
          var form = document.querySelector('form[action="/cart/add"]');
          if (form) {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        });
      }
    },
  };

  /* ------------------------------------------------------------------ */
  /*  AnnouncementBar                                                    */
  /* ------------------------------------------------------------------ */

  var AnnouncementBar = {
    init: function () {
      var bar = document.querySelector('[data-announcement]');
      var dismiss = document.querySelector('[data-announcement-dismiss]');
      if (!bar) return;

      if (sessionStorage.getItem('basal-announcement-dismissed') === 'true') {
        bar.style.display = 'none';
        return;
      }

      if (dismiss) {
        dismiss.addEventListener('click', function () {
          bar.style.display = 'none';
          sessionStorage.setItem('basal-announcement-dismissed', 'true');
        });
      }
    },
  };

  /* ------------------------------------------------------------------ */
  /*  SmoothScroll                                                       */
  /* ------------------------------------------------------------------ */

  var SmoothScroll = {
    init: function () {
      document.addEventListener('click', function (e) {
        var link = e.target.closest('a[href^="#"]');
        if (!link) return;
        var hash = link.getAttribute('href');
        if (hash.length <= 1) return;

        var target = document.querySelector(hash);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth' });
          /* Update URL hash without jump */
          if (window.history && window.history.pushState) {
            window.history.pushState(null, '', hash);
          }
        }
      });
    },
  };

  /* ------------------------------------------------------------------ */
  /*  HeaderScroll                                                       */
  /* ------------------------------------------------------------------ */

  var HeaderScroll = {
    init: function () {
      var header = document.querySelector('[data-header]');
      if (!header) return;

      var threshold = 50;
      var ticking = false;

      window.addEventListener('scroll', function () {
        if (!ticking) {
          window.requestAnimationFrame(function () {
            if (window.scrollY > threshold) {
              header.classList.add('is-scrolled');
            } else {
              header.classList.remove('is-scrolled');
            }
            ticking = false;
          });
          ticking = true;
        }
      });
    },
  };

  /* ------------------------------------------------------------------ */
  /*  Form Handling (Product form ATC intercept)                         */
  /* ------------------------------------------------------------------ */

  var FormHandler = {
    init: function () {
      document.addEventListener('submit', function (e) {
        var form = e.target.closest('form[action="/cart/add"]');
        if (!form) return;

        e.preventDefault();

        var select = form.querySelector('select[name="id"], input[name="id"]');
        if (!select || !select.value) return;

        var variantId = parseInt(select.value, 10);
        var qtyInput = form.querySelector('input[name="quantity"]');
        var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

        var submitBtn = form.querySelector('[type="submit"]');
        var originalText = '';
        if (submitBtn) {
          originalText = submitBtn.textContent;
          submitBtn.textContent = 'Adding...';
          submitBtn.disabled = true;
        }

        CartAPI.addToCart(variantId, quantity)
          .then(function () {
            return CartAPI.getCart();
          })
          .then(function (cart) {
            CartDrawer.render(cart);
            CartDrawer.open();
          })
          .catch(function (err) {
            console.error('Add to cart failed:', err);
          })
          .finally(function () {
            if (submitBtn) {
              submitBtn.textContent = originalText;
              submitBtn.disabled = false;
            }
          });
      });
    },
  };

  /* ------------------------------------------------------------------ */
  /*  Cart Toggle (header link opens drawer instead of navigating)       */
  /* ------------------------------------------------------------------ */

  var CartToggle = {
    init: function () {
      document.addEventListener('click', function (e) {
        var toggle = e.target.closest('[data-cart-toggle]');
        if (!toggle) return;
        e.preventDefault();
        CartDrawer.refresh();
        CartDrawer.open();
      });
    },
  };

  /* ------------------------------------------------------------------ */
  /*  MobileMenu                                                         */
  /* ------------------------------------------------------------------ */

  var MobileMenu = {
    init: function () {
      var toggle = document.querySelector('[data-menu-toggle]');
      var nav = document.querySelector('[data-mobile-nav]');
      if (!toggle || !nav) return;

      toggle.addEventListener('click', function () {
        var open = nav.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open);
      });

      /* Close on link click */
      nav.querySelectorAll('.header__link').forEach(function (link) {
        link.addEventListener('click', function () {
          nav.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
        });
      });
    },
  };

  /* ------------------------------------------------------------------ */
  /*  Init                                                               */
  /* ------------------------------------------------------------------ */

  document.addEventListener('DOMContentLoaded', function () {
    CartDrawer.init();
    CartToggle.init();
    FormHandler.init();
    StickyATC.init();
    AnnouncementBar.init();
    SmoothScroll.init();
    HeaderScroll.init();
    MobileMenu.init();
  });
})();
